/**
 * Umleitungen der alten `/veranstaltungen/...`-Adressen auf die neuen `/vorgaenge/...`- bzw.
 * `/buchungen`-Adressen (Arbeitspaket F0, docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md Teil B2,
 * Antoines Entscheidung D3, 12.09.2026).
 *
 * AUSNAHME, wird hier NICHT umgeleitet: `/veranstaltungen/:id/angebotsseite` — die oeffentliche
 * Angebotsseite steht bereits in versendeten Kunden-E-Mails und bleibt woertlich bestehen. Sie
 * ist in `App.tsx` als eigene, spezifischere Route VOR der Splat-Route `/veranstaltungen/*`
 * registriert; React Router waehlt beim Matching automatisch die spezifischere Route, unabhaengig
 * von der Deklarationsreihenfolge (geprueft in `test/umleitungen.test.tsx`).
 */
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { BUCHUNGEN_PFAD, vorgangPfad } from "../lib/pfade";
import { EmptyState, LoadingRows } from "./components";

/**
 * `/veranstaltungen/*` → `/vorgaenge/<rest>` (Query-String erhalten), bzw. ohne Rest
 * (`/veranstaltungen` selbst) → `/buchungen`.
 */
export function VeranstaltungenUmleitung() {
  const { "*": rest } = useParams();
  const { search } = useLocation();
  const ziel = rest ? `/vorgaenge/${rest}` : BUCHUNGEN_PFAD;
  return <Navigate to={`${ziel}${search}`} replace />;
}

/**
 * Lern-Übersicht (Antoines Freigabe 25.09.2026): die Seite „Wissen“ heißt jetzt „Gedächtnis“ und
 * liegt unter `/gedaechtnis`. Alte Links (`/wissen`, `/wissen?tab=lernt`, `/wissen?q=…`) bleiben
 * gültig - Query-String erhalten, damit Reiter und Suche mitkommen.
 */
export function WissenUmleitung() {
  const { search } = useLocation();
  return <Navigate to={`/gedaechtnis${search}`} replace />;
}

/**
 * F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md Teil B2, Antoines Entscheidung D3): ersetzt die
 * geloeschte `pages/anfrage-detail.tsx` unter `/anfragen/:id`. Kein Sonderfall mehr, sondern ein
 * reiner Umleitungs-Resolver — er loest `:id` (egal ob Anfrage- ODER bereits Event-ID, der Backend-
 * Vertrag akzeptiert beides) ueber den bereits live gelieferten Endpunkt
 * `GET /api/inquiries/:id/vorgang` auf die eine Vorgangsseite auf. Der Query-String bleibt
 * erhalten (z. B. `?notiz=1` aus `AnfragenKanban`s "Notiz hinzufügen").
 *
 * Erzeuger dieses Links: `NotificationsBell.tsx`, `customer-detail.tsx` (Historie),
 * `lib/tasks-board.ts` (Backend-Deep-Links), `@maestro/anfrage-stand` (`aktionBauen`-Fallback,
 * s. Kommentar dort) sowie `pages/inquiries.tsx#?open=` fuer alte Lesezeichen.
 */
export function AnfrageUmleitung() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const { data, loading, error } = useApi<{ eventId: string }>(id ? `/api/inquiries/${id}/vorgang` : null);

  if (loading) return <LoadingRows />;
  if (error || !data) {
    return (
      <EmptyState
        icon="search_off"
        title="Anfrage nicht gefunden"
        hint="Der Link ist ungültig, oder der Vorgang wurde inzwischen gelöscht."
      />
    );
  }
  return <Navigate to={`${vorgangPfad(data.eventId)}${search}`} replace />;
}
